import { useState, useMemo } from "react";
import { DateTime } from "luxon";
import { Container, Box, CircularProgress, Alert } from "@mui/material";
import { useTaskerMessageContext } from "../TaskerMessageContext";
import { MessagesByDay } from "./MyMessagesTypes";
import { DayDialog } from "./DayDialog";
import { LoadMoreButton, NoMoreItemsIndicator } from "@/components/common";
import { MessagesTimeline } from "./MessagesTimeline";
import { TaskerHeader } from "./TaskerHeader";
import { StatsCards } from "./StatsCards";

export const TaskerMyMessages = () => {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const {
    taskerMyMessages,
    loading,
    error,
    loadMoreMessages,
    hasMoreMessages,
  } = useTaskerMessageContext();

  const messagesByDay = useMemo(() => {
    const grouped: MessagesByDay = {};

    taskerMyMessages.forEach((message) => {
      const messageDate = message.timestamp;
      const date = messageDate.toISODate() as string;

      if (!grouped[date]) {
        grouped[date] = {
          messages: [],
          approved: 0,
          rejected: 0,
          pending: 0,
          hasMedia: 0,
        };
      }

      grouped[date].messages.push(message);

      if (message.reviewStatus === "approved") {
        grouped[date].approved += 1;
      } else if (message.reviewStatus === "rejected") {
        grouped[date].rejected += 1;
      } else {
        grouped[date].pending += 1;
      }

      if (message.mediaFiles && message.mediaFiles.length > 0) {
        grouped[date].hasMedia += 1;
      }
    });

    return Object.entries(grouped)
      .sort(
        ([dateA], [dateB]) =>
          DateTime.fromISO(dateB).toMillis() -
          DateTime.fromISO(dateA).toMillis(),
      )
      .reduce((acc, [date, data]) => {
        acc[date] = data;
        return acc;
      }, {} as MessagesByDay);
  }, [taskerMyMessages]);

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Error loading messages: {error.message}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 0 } }}>
      {loading && taskerMyMessages.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress size={40} />
        </Box>
      ) : taskerMyMessages.length === 0 ? (
        <Alert
          severity="info"
          sx={{
            my: 3,
            borderRadius: 1.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          You have not submitted any messages yet.
        </Alert>
      ) : (
        <>
          <TaskerHeader />

          <StatsCards />

          <MessagesTimeline
            messagesByDay={messagesByDay}
            handleOpenDayDialog={(date) => setSelectedDay(date)}
          />

          {hasMoreMessages ? (
            <LoadMoreButton
              loadMoreMessages={loadMoreMessages}
              loading={loading}
            />
          ) : (
            <NoMoreItemsIndicator
              message="You've reached the end"
              subMessage="No more messages to load"
            />
          )}
        </>
      )}

      {selectedDay && (
        <DayDialog
          handleCloseDayDialog={() => setSelectedDay(null)}
          selectedDay={selectedDay}
          messagesByDay={messagesByDay}
        />
      )}
    </Container>
  );
};
